<template>
  <div class="hr-workbench-page">
    <div class="dash-header">
      <div class="dash-header-top">
        <div class="dash-header-left">
          <h1 class="dash-title">外管工作台</h1>
          <p class="dash-sub">Foreign Teacher HR Workbench / 试用期 / 新外教回访 / 合同续签 / 出勤跟进 / 异动高危</p>
        </div>
        <div class="dash-meta">
          <div class="dash-meta-row" v-for="item in metaRows" :key="item.label">
            <span class="dash-meta-label">{{ item.label }}</span>
            <span class="dash-meta-value">{{ item.value }}</span>
          </div>
        </div>
      </div>
      <div class="dash-chips">
        <span class="dash-chip" v-for="chip in chips" :key="chip.label">
          <i class="chip-icon"></i>
          <span class="chip-label">{{ chip.label }}</span>
          <span class="chip-value">{{ chip.value }}</span>
        </span>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">核心指标</h2>
        <span class="section-legend">
          <i class="legend-dot is-danger"></i>红=预警
          / <i class="legend-dot is-warning"></i>黄=关注
          / <i class="legend-dot is-healthy"></i>绿=健康
        </span>
        <span class="section-adviser">
          <span class="section-adviser-label">外教运营</span>
          <adviser-select v-model="adviserId" @on-change="onAdviserChange" @on-loaded="advisers = $event"></adviser-select>
        </span>
      </div>
      <div class="section-filters">
        <span class="filter-item"><i class="legend-dot is-healthy"></i>健康</span>
        <span class="filter-item"><i class="legend-dot is-warning"></i>需关注</span>
        <span class="filter-item"><i class="legend-dot is-danger"></i>预警</span>
        <span class="filter-note">※ 口径：在职外教（在职 + 外教），按服务器当日自然日计算 · 点击卡片进入对应页面</span>
      </div>
      <div class="kpi-grid">
        <Spin v-if="loading" fix></Spin>
        <article v-for="card in kpiCards" :key="card.key" class="kpi-card is-clickable"
                 :class="'is-' + card.level" @click="goCard(card)">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              <span>{{ card.title }}</span>
            </div>
            <div class="kpi-value">{{ card.value }}</div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ card.period }}</span>
              <span v-if="card.compare" class="kpi-compare" :class="'is-' + card.compareTone">{{ card.compare }}</span>
            </div>
            <p class="kpi-foot">{{ card.foot }}</p>
          </div>
        </article>
      </div>
    </div>

    <div class="dash-section dash-two-col">
      <div class="dash-block">
        <div class="section-head">
          <h2 class="section-title">今日待办</h2>
          <span class="section-legend">
            <i class="legend-dot is-danger"></i>紧急
            / <i class="legend-dot is-warning"></i>关注
            / <i class="legend-dot is-info"></i>预告
          </span>
          <span class="block-note">{{ todayStr }} · 共 {{ todos.length }} 项{{ todos.length > 16 ? '，显示前 16 项' : '' }}</span>
        </div>
        <p class="block-lead">试用期、合同、昨日出勤待跟进、新外教回访、异动高危，按紧急 → 关注 → 预告排序。</p>
        <div class="card">
          <div v-if="!shownTodos.length" class="todo-empty">
            <div class="todo-empty-big">今日暂无待办</div>
            <div>所有提醒均已处理完毕</div>
          </div>
          <div v-for="(t, idx) in shownTodos" :key="t.kind + '-' + t.tchId + '-' + idx" class="todo-row">
            <span class="todo-bar" :class="'is-' + (t.severity || 'info')"></span>
            <div class="todo-main">
              <div class="todo-title">
                <span class="tch-name-row">
                  <a href="javascript:;" @click="openTchInfo(t.tchId)">{{ t.title || tchText(t) }}</a>
                  <span v-if="scoreText(t)" class="tch-score-badge">{{ scoreText(t) }}</span>
                </span>
                <span class="todo-kind" :class="'is-' + (t.severity || 'info')">{{ kindText(t.kind) }}</span>
              </div>
              <div class="todo-meta">{{ t.meta }}</div>
              <div v-if="t.action" class="todo-action">建议：{{ t.action }}</div>
            </div>
            <div class="todo-btns">
              <Button size="small" @click="openTchInfo(t.tchId)">档案</Button>
              <Button size="small" type="primary" :loading="todoLoadingKey === todoKey(t)" @click="handleTodo(t)">
                {{ kindButton(t.kind) }}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div class="dash-block">
        <div class="section-head">
          <h2 class="section-title">近 7 日异动趋势</h2>
        </div>
        <p class="block-lead">每天请假 + 旷课（含失联）课节数，含今天。</p>
        <div class="card trend-card">
          <div class="trend">
            <div v-for="d in trendBars" :key="d.date" class="trend-col">
              <span class="trend-num">{{ d.count }}</span>
              <div class="trend-bar" :class="{ 'is-yesterday': d.isYesterday }" :style="{ height: d.height + 'px' }"></div>
              <span class="trend-label">{{ d.label }}</span>
            </div>
          </div>
        </div>

        <div class="section-head block-gap">
          <h2 class="section-title">试用期状态分布</h2>
        </div>
        <div class="card">
          <div v-for="item in probationBars" :key="item.key" class="dist-row">
            <div class="dist-head">
              <span>{{ item.label }}</span>
              <b>{{ item.value }} 人</b>
            </div>
            <div class="dist-track"><div class="dist-fill" :class="'is-' + item.tone" :style="{ width: item.percent + '%' }"></div></div>
          </div>
        </div>

        <div class="section-head block-gap">
          <h2 class="section-title">合同状态分布</h2>
        </div>
        <div class="card">
          <div v-for="item in contractBars" :key="item.key" class="dist-row">
            <div class="dist-head">
              <span>{{ item.label }}</span>
              <b>{{ item.value }} 人</b>
            </div>
            <div class="dist-track"><div class="dist-fill" :class="'is-' + item.tone" :style="{ width: item.percent + '%' }"></div></div>
          </div>
        </div>
      </div>
    </div>

    <probation-decide-modal :value="modal.probation" :row="modalRow"
                            @on-close="modal.probation = false" @on-success="loadSummary"></probation-decide-modal>
    <review-save-modal :value="modal.review" :row="modalRow"
                       @on-close="modal.review = false" @on-success="loadSummary"></review-save-modal>
    <contract-renew-modal :value="modal.contract" :row="modalRow"
                          @on-close="modal.contract = false" @on-success="loadSummary"></contract-renew-modal>
    <tch-info-modal :value="showTchInfoModal" :tchId="tchInfoId" @on-close="showTchInfoModal = false"></tch-info-modal>
  </div>
</template>
<script>
import AdviserSelect from './AdviserSelect.vue'
import ProbationDecideModal from './modal/ProbationDecideModal.vue'
import ReviewSaveModal from './modal/ReviewSaveModal.vue'
import ContractRenewModal from './modal/ContractRenewModal.vue'
import TchInfoModal from '../teacher/modal/TchInfoModal.vue'
import { API_PREFIX, TODO_KIND, tchLabel, adviserParam, resolveAdviserId, writeStoredAdviserId} from './constants'
import { fetchTchScoreMap, applyTchScoresToRows } from '@/utils/tchScore'
const SEVERITY_ORDER = { danger: 0, warning: 1, info: 2 }
const TODO_LIST_API = {
  PROBATION: '/probation/list',
  CONTRACT: '/contract/list',
  REVIEW: '/review/list'
}
function num (v) {
  return typeof v === 'number' && !isNaN(v) ? v : 0
}
function pendingLevel (danger, warning) {
  if (danger > 0) {
    return 'danger'
  }
  return warning > 0 ? 'warning' : 'healthy'
}
export default {
  name: 'HrWorkbenchDashboard',
  components: { AdviserSelect, ProbationDecideModal, ReviewSaveModal, ContractRenewModal, TchInfoModal },
  data () {
    let q = this.$route.query || {}
    return {
      adviserId: resolveAdviserId(q),
      advisers: [],
      loading: false,
      loadedAt: '',
      todayStr: this.$moment().format('YYYY-MM-DD'),
      yesterdayStr: this.$moment().subtract(1, 'days').format('YYYY-MM-DD'),
      rules: {},
      kpi: {},
      todos: [],
      rankScoreSeq: 0,
      trend: [],
      probationDist: {},
      contractDist: {},
      modal: {
        probation: false,
        review: false,
        contract: false
      },
      modalRow: null,
      todoLoadingKey: '',
      showTchInfoModal: false,
      tchInfoId: null
    }
  },
  computed: {
    adviserName () {
      if (this.adviserId === 'all') {
        return '全部运营'
      }
      let id = this.adviserId
      let hit = this.advisers.filter(function (a) {
        return String(a.id) === id
      })[0]
      return hit ? (hit.nickname || '未分配') : id
    },
    metaRows () {
      return [
        { label: '数据来源', value: '在职外教 · 处理记录' },
        { label: '口径版本', value: '规则与设置（实时）' },
        { label: '最近更新', value: this.loadedAt || '—' }
      ]
    },
    chips () {
      let r = this.rules
      return [
        { label: '今天', value: this.todayStr },
        { label: '外教运营', value: this.adviserName },
        { label: '试用期', value: (r.probationMonths || '—') + ' 个月' },
        { label: '合同提醒', value: (r.contractAlertDays || '—') + ' 天' },
        { label: '回访节点', value: (r.reviewDays || '—') + ' 天' }
      ]
    },
    kpiCards () {
      let k = this.kpi
      let r = this.rules
      let pExpired = num(k.probationExpired)
      let pUrgent = num(k.probationUrgent)
      let rOverdue = num(k.reviewOverdue)
      let rSoon = num(k.reviewSoon)
      let cExpiring = num(k.contractExpiring)
      let cExpired = num(k.contractExpired)
      let fTeachers = num(k.followupTeachers)
      let fLessons = num(k.followupPendingLessons)
      let risk = num(k.riskHighCount)
      let clean = num(k.cleanCount)
      let pop = num(k.riskPopulation)
      let cleanRate = pop > 0 ? Math.round(clean / pop * 100) : null
      let today = '截至 ' + this.todayStr
      return [
        {
          key: 'active',
          title: '在职外教',
          value: num(k.activeCount),
          level: 'healthy',
          period: today,
          compare: '试用期 ' + num(k.probationCount) + ' 人',
          compareTone: 'neutral',
          foot: '含试用期',
          to: '/hrWorkbench/contract',
          tab: 'all'
        },
        {
          key: 'probationCount',
          title: '试用期中',
          value: num(k.probationCount),
          level: 'healthy',
          period: today,
          compare: '',
          foot: '试用期 ' + (r.probationMonths || '—') + ' 个月',
          to: '/hrWorkbench/probation',
          tab: 'all'
        },
        {
          key: 'probationPending',
          title: '试用期待处理',
          value: pExpired + pUrgent,
          level: pendingLevel(pExpired, pExpired + pUrgent),
          period: today,
          compare: pExpired ? '逾期 ' + pExpired + ' 人' : '',
          compareTone: 'danger',
          foot: '逾期 ' + pExpired + ' · ' + (r.probationUrgentDays || '—') + ' 天内 ' + pUrgent,
          to: '/hrWorkbench/probation',
          tab: 'open'
        },
        {
          key: 'review',
          title: '新外教待回访',
          value: rOverdue + rSoon,
          level: pendingLevel(rOverdue, rOverdue + rSoon),
          period: today,
          compare: rOverdue ? '逾期 ' + rOverdue + ' 人' : '',
          compareTone: 'danger',
          foot: '逾期 ' + rOverdue + ' · 即将 ' + rSoon,
          to: '/hrWorkbench/review',
          tab: 'open'
        },
        {
          key: 'contractExpiring',
          title: '合同 ' + (r.contractAlertDays || '—') + ' 天内到期',
          value: cExpiring,
          level: cExpiring > 0 ? 'warning' : 'healthy',
          period: today,
          compare: '',
          foot: '需发起续签沟通',
          to: '/hrWorkbench/contract',
          tab: 'expiring'
        },
        {
          key: 'contractExpired',
          title: '合同已超期',
          value: cExpired,
          level: cExpired > 0 ? 'danger' : 'healthy',
          period: today,
          compare: '',
          foot: '存在用工合规风险',
          to: '/hrWorkbench/contract',
          tab: 'expired'
        },
        {
          key: 'followup',
          title: '待跟进老师',
          value: fTeachers,
          level: fTeachers > 0 ? 'danger' : 'healthy',
          period: '昨日 ' + this.yesterdayStr,
          compare: fLessons ? fLessons + ' 节待跟进' : '',
          compareTone: 'danger',
          foot: '昨日请假/旷课 ' + fLessons + ' 节待跟进',
          to: '/hrWorkbench/followup'
        },
        {
          key: 'cleanRate',
          title: '零瑕疵占比',
          value: cleanRate == null ? '—' : (cleanRate + '%'),
          level: cleanRate == null ? 'healthy' : (cleanRate >= 80 ? 'healthy' : (cleanRate >= 50 ? 'warning' : 'danger')),
          period: pop > 0 ? (clean + ' / ' + pop + ' 人') : '在职外教',
          compare: '',
          foot: '风险分低于异动线',
          to: '/tch/tchRiskWarningList',
          external: true
        },
        {
          key: 'risk',
          title: '异动高危老师',
          value: risk,
          level: risk > 0 ? 'danger' : 'healthy',
          period: '外教异动风险监测',
          compare: '',
          foot: '点开去外教异动风险监测',
          to: '/tch/tchRiskWarningList',
          external: true
        }
      ]
    },
    shownTodos () {
      let list = this.todos.slice()
      list.sort(function (a, b) {
        let wa = SEVERITY_ORDER[a.severity] == null ? 3 : SEVERITY_ORDER[a.severity]
        let wb = SEVERITY_ORDER[b.severity] == null ? 3 : SEVERITY_ORDER[b.severity]
        return wa - wb
      })
      return list.slice(0, 16)
    },
    trendBars () {
      let self = this
      let max = 1
      this.trend.forEach(function (d) {
        if (num(d.count) > max) {
          max = num(d.count)
        }
      })
      return this.trend.map(function (d) {
        let label = d.date ? d.date.slice(5) : ''
        if (d.date === self.todayStr) {
          label = '今日'
        } else if (d.date === self.yesterdayStr) {
          label = '昨日'
        }
        return {
          date: d.date,
          count: num(d.count),
          label: label,
          isYesterday: d.date === self.yesterdayStr,
          height: Math.round(num(d.count) / max * 110) + 4
        }
      })
    },
    probationBars () {
      let d = this.probationDist
      let r = this.rules
      return this.toBars([
        { key: 'expired', label: '已逾期，待结论', value: num(d.expired), tone: 'danger' },
        { key: 'urgent', label: (r.probationUrgentDays || '—') + ' 天内到期', value: num(d.urgent), tone: 'warning' },
        { key: 'near', label: (r.probationNearDays || '—') + ' 天内到期', value: num(d.near), tone: 'info' },
        { key: 'normal', label: '试用期中（未触发）', value: num(d.normal), tone: 'healthy' }
      ])
    },
    contractBars () {
      let d = this.contractDist
      return this.toBars([
        { key: 'expired', label: '已超期', value: num(d.expired), tone: 'danger' },
        { key: 'expiring', label: (this.rules.contractAlertDays || '—') + ' 天内到期', value: num(d.expiring), tone: 'warning' },
        { key: 'normal', label: '有效期内', value: num(d.normal), tone: 'healthy' }
      ])
    }
  },
  mounted () {
    writeStoredAdviserId(this.adviserId)
    this.$root.$emit('hr-badges-refresh')

    this.loadSummary()
  },
  methods: {
    toBars (list) {
      let total = list.reduce(function (n, item) {
        return n + item.value
      }, 0) || 1
      return list.map(function (item) {
        return Object.assign({}, item, { percent: Math.round(item.value / total * 1000) / 10 })
      })
    },
    tchText (t) {
      return tchLabel(t)
    },
    kindText (kind) {
      return (TODO_KIND[kind] && TODO_KIND[kind].text) || kind || ''
    },
    kindButton (kind) {
      return (TODO_KIND[kind] && TODO_KIND[kind].button) || '处理'
    },
    todoKey (t) {
      return t.kind + '-' + t.tchId
    },
    onAdviserChange () {
      writeStoredAdviserId(this.adviserId)
      this.$root.$emit('hr-badges-refresh')
      this.loadSummary()
    },
    loadSummary () {
      let self = this
      self.loading = true
      self.$http.post(API_PREFIX + '/summary', adviserParam(self.adviserId)).then(function (res) {
        if (res && res.status === 'ok' && res.data) {
          let d = res.data
          self.rules = d.rules || {}
          self.kpi = d.kpi || {}
          self.todos = d.todos || []
          self.trend = d.trend || []
          self.probationDist = d.probationDist || {}
          self.contractDist = d.contractDist || {}
          self.loadedAt = self.$moment().format('YYYY-MM-DD HH:mm:ss')
          self.loadMonthRankScores()
        }
      }).finally(function () {
        self.loading = false
      })
    },
    scoreText (t) {
      if (!t || t.monthRankScore == null || t.monthRankScore === '') {
        return ''
      }
      const num = Number(t.monthRankScore)
      if (isNaN(num)) {
        return ''
      }
      return num.toFixed(2)
    },
    loadMonthRankScores () {
      const rows = this.todos || []
      if (!rows.length) {
        return
      }
      const ids = rows.map(function (row) { return row.tchId }).filter(function (id) { return id != null })
      const seq = ++this.rankScoreSeq
      const self = this
      fetchTchScoreMap(this.$http, ids).then(function (map) {
        if (seq !== self.rankScoreSeq) {
          return
        }
        applyTchScoresToRows(self, rows, 'tchId', 'monthRankScore', map)
      })
    },
    routeQuery (extra) {
      let q = Object.assign({}, extra || {})
      if (this.adviserId !== 'all') {
        q.studyAdviserId = this.adviserId
      }
      return q
    },
    goCard (card) {
      if (card.external) {
        this.$router.push(card.to)
        return
      }
      this.$router.push({ path: card.to, query: this.routeQuery(card.tab ? { level: card.tab } : null) })
    },
    handleTodo (t) {
      if (t.kind === 'RISK') {
        this.$router.push('/tch/tchRiskWarningList')
        return
      }
      if (t.kind === 'FOLLOWUP') {
        this.$router.push({
          path: '/hrWorkbench/followup',
          query: this.routeQuery({ keyword: t.tchName || String(t.tchId) })
        })
        return
      }
      let api = TODO_LIST_API[t.kind]
      if (!api) {
        return
      }
      let self = this
      let key = self.todoKey(t)
      self.todoLoadingKey = key
      let params = Object.assign({ level: 'open' }, adviserParam(self.adviserId))
      self.$http.post(API_PREFIX + api, params).then(function (res) {
        let rows = (res && res.status === 'ok' && res.data && res.data.rows) || []
        let row = rows.filter(function (r) {
          return r.tchId === t.tchId
        })[0]
        self.openTodoModal(t.kind, row || { tchId: t.tchId, tchName: t.tchName })
      }).finally(function () {
        if (self.todoLoadingKey === key) {
          self.todoLoadingKey = ''
        }
      })
    },
    openTodoModal (kind, row) {
      this.modalRow = row
      if (kind === 'PROBATION') {
        this.modal.probation = true
      } else if (kind === 'CONTRACT') {
        this.modal.contract = true
      } else if (kind === 'REVIEW') {
        this.modal.review = true
      }
    },
    openTchInfo (tchId) {
      if (!tchId) {
        return
      }
      this.tchInfoId = tchId
      this.showTchInfoModal = true
    }
  }
}
</script>
<style scoped lang="less">
@page-bg: #F5F6FA;
@header-bg: #2A3C62;
@header-bg-end: #314E78;
@text: #1A1A1A;
@text-title: #303133;
@text-muted: #8B93A7;
@text-desc: #9AA3B2;
@card: #FFFFFF;
@danger: #E6464E;
@warning: #E89F2D;
@healthy: #0B9E8D;
@info: #5B8FF9;
@radius-header: 16px;
@radius-card: 12px;
@gap: 16px;
@card-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px rgba(15, 23, 42, .04);

.hr-workbench-page {
  margin: -24px;
  min-height: calc(100vh - 108px);
  padding: 16px;
  background: @page-bg;
}

.dash-header {
  padding: 20px 24px 16px;
  border-radius: @radius-header;
  background: linear-gradient(90deg, @header-bg 0%, @header-bg-end 100%);
  color: #fff;
}

.dash-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: @gap;
}

.dash-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #fff;
}

.dash-sub {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, .7);
}

.dash-meta {
  text-align: right;
  white-space: nowrap;
}

.dash-meta-row {
  font-size: 12px;
  line-height: 1.7;
}

.dash-meta-label {
  margin-right: 8px;
  color: rgba(255, 255, 255, .55);
}

.dash-meta-value {
  color: rgba(255, 255, 255, .92);
}

.dash-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.dash-chip {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(255, 255, 255, .12);
  border-radius: 16px;
  background: rgba(255, 255, 255, .08);
  font-size: 12px;
  color: rgba(255, 255, 255, .88);
}

.chip-icon {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  border: 1px solid rgba(255, 255, 255, .55);
  border-radius: 50%;
}

.chip-label {
  margin-right: 6px;
}

.chip-value {
  font-weight: 600;
}

.dash-section {
  margin-top: 18px;
}

.section-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.section-title {
  margin: 0;
  padding-left: 10px;
  border-left: 4px solid @header-bg;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: @text;
}

.section-legend {
  font-size: 12px;
  color: @text-muted;
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px 0 8px;
  border-radius: 50%;
  vertical-align: 1px;
  &.is-danger {
    background: @danger;
  }
  &.is-warning {
    background: @warning;
  }
  &.is-healthy {
    background: @healthy;
  }
  &.is-info {
    background: @info;
  }
}

.section-adviser {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
}

.section-adviser-label {
  margin-right: 8px;
  font-size: 13px;
  color: @text-title;
}

.section-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
  font-size: 13px;
  color: @text-title;
}

.filter-item {
  display: inline-flex;
  align-items: center;
  .legend-dot {
    margin-left: 0;
    margin-right: 6px;
  }
}

.filter-note {
  margin-left: auto;
  font-size: 12px;
  color: @text-muted;
}

.kpi-grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: @gap;
  margin-top: 16px;
}

.kpi-card {
  position: relative;
  display: flex;
  background: @card;
  border-radius: @radius-card;
  box-shadow: @card-shadow;
  overflow: hidden;
  &.is-clickable {
    cursor: pointer;
  }
  &.is-clickable:hover {
    box-shadow: 0 1px 2px rgba(15, 23, 42, .06), 0 10px 28px rgba(15, 23, 42, .08);
  }
}

.kpi-bar {
  width: 4px;
  flex-shrink: 0;
  background: #D8DEE8;
}

.kpi-card.is-danger .kpi-bar,
.kpi-card.is-danger .kpi-dot {
  background: @danger;
}

.kpi-card.is-warning .kpi-bar,
.kpi-card.is-warning .kpi-dot {
  background: @warning;
}

.kpi-card.is-healthy .kpi-bar,
.kpi-card.is-healthy .kpi-dot {
  background: @healthy;
}

.kpi-body {
  flex: 1;
  min-width: 0;
  padding: 16px 18px 14px;
}

.kpi-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  line-height: 1.4;
  color: @text-title;
}

.kpi-dot {
  display: inline-block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
}

.kpi-value {
  margin-top: 18px;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.2;
  color: @text;
}

.kpi-period-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: @text-muted;
}

.kpi-period {
  min-width: 0;
  flex: 1 1 auto;
}

.kpi-compare {
  flex-shrink: 0;
  font-weight: 600;
  white-space: nowrap;
  color: @text-muted;
  &.is-danger {
    color: @danger;
  }
  &.is-healthy {
    color: @healthy;
  }
}

.kpi-foot {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #EEF0F4;
  font-size: 12px;
  line-height: 1.5;
  color: @text-desc;
}

.dash-two-col {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: @gap;
}

.block-note {
  margin-left: auto;
  font-size: 12px;
  color: @text-muted;
}

.block-lead {
  margin: 8px 0 12px;
  font-size: 12px;
  color: @text-muted;
}

.block-gap {
  margin-top: 18px;
  margin-bottom: 12px;
}

.card {
  padding: 8px 18px;
  background: @card;
  border-radius: @radius-card;
  box-shadow: @card-shadow;
}

.todo-empty {
  padding: 40px 0;
  text-align: center;
  font-size: 12px;
  color: @text-muted;
}

.todo-empty-big {
  margin-bottom: 6px;
  font-size: 15px;
  color: @text-title;
}

.todo-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #EEF0F4;
  &:last-child {
    border-bottom: 0;
  }
}

.todo-bar {
  align-self: stretch;
  flex-shrink: 0;
  width: 4px;
  border-radius: 2px;
  &.is-danger {
    background: @danger;
  }
  &.is-warning {
    background: @warning;
  }
  &.is-info {
    background: @info;
  }
}

.todo-main {
  flex: 1;
  min-width: 0;
}

.todo-title {
  font-size: 13px;
  font-weight: 600;
  color: @text-title;
}

.todo-kind {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: normal;
  line-height: 18px;
  &.is-danger {
    color: @danger;
    background: rgba(230, 70, 78, .1);
  }
  &.is-warning {
    color: @warning;
    background: rgba(232, 159, 45, .12);
  }
  &.is-info {
    color: @info;
    background: rgba(91, 143, 249, .12);
  }
}

.todo-meta {
  margin-top: 4px;
  font-size: 12px;
  color: @text-muted;
  word-break: break-word;
}

.todo-action {
  margin-top: 2px;
  font-size: 12px;
  color: @text-title;
}

.todo-btns {
  flex-shrink: 0;
  white-space: nowrap;
  .ivu-btn + .ivu-btn {
    margin-left: 6px;
  }
}

.trend-card {
  padding: 16px 18px 12px;
}

.trend {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  height: 160px;
}

.trend-col {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
}

.trend-num {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: @text-title;
}

.trend-bar {
  width: 60%;
  max-width: 36px;
  border-radius: 4px 4px 0 0;
  background: #C9D3E6;
  &.is-yesterday {
    background: @header-bg-end;
  }
}

.trend-label {
  margin-top: 6px;
  font-size: 12px;
  color: @text-muted;
}

.dist-row {
  padding: 8px 0;
}

.dist-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 12px;
  color: @text-title;
}

.dist-track {
  height: 8px;
  border-radius: 4px;
  background: #EEF0F4;
  overflow: hidden;
}

.dist-fill {
  height: 100%;
  border-radius: 4px;
  &.is-danger {
    background: @danger;
  }
  &.is-warning {
    background: @warning;
  }
  &.is-info {
    background: @info;
  }
  &.is-healthy {
    background: @healthy;
  }
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .dash-two-col {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hr-workbench-page {
    margin: -16px;
    padding: 12px;
  }
  .dash-header-top {
    flex-direction: column;
  }
  .dash-meta {
    text-align: left;
    white-space: normal;
  }
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.tch-name-row {
  display: inline-flex;
  align-items: flex-start;
  white-space: nowrap;
  margin-right: 6px;
}
.tch-score-badge {
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 7px;
  background: #ff9900;
  color: #fff;
  font-size: 10px;
  line-height: 14px;
}
</style>



// WEBPACK FOOTER //
// HrWorkbenchDashboard.vue?5061c069